import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, money, imgUrl } from "@/lib/api";
import { FadeUp, Eyebrow } from "@/components/Motion";
import PageHero from "@/components/PageHero";
import { useSiteMedia } from "@/context/SiteMediaContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { useSeo } from "@/lib/useSeo";

const fmtTime = (t) => {
  if (!t) return "";
  const [h, mn] = t.split(":").map(Number);
  if (Number.isNaN(h)) return t;
  const ap = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(mn).padStart(2, "0")} ${ap}`;
};
const fmtRange = (a, b) => {
  if (a && b) return `${fmtTime(a)} – ${fmtTime(b)}`;
  return fmtTime(a) || fmtTime(b);
};

export default function Programs() {
  const { m } = useSiteMedia();
  const { c, media } = useSiteContent();
  useSeo(c("seo_programs_title"), c("seo_programs_desc"));
  const hero = media["programs_hero"] || {};
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="programs-page">
      <PageHero
        eyebrow={c("programs_hero_eyebrow", "Programs · Ages 3–5")}
        title={c("programs_hero_title", "The Wonder Studio experience.")}
        image={imgUrl(m("programs_hero", "/gallery/photo3.webp"))}
        overlay={hero.overlay}
        focal={hero.focal}
      />

      {/* WONDER STUDIO OVERVIEW */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <FadeUp>
          {c("programs_overview_eyebrow") && <Eyebrow>{c("programs_overview_eyebrow")}</Eyebrow>}
          <p className="font-serif text-3xl md:text-4xl font-light leading-[1.3] max-w-3xl mt-4">
            {c("programs_overview_text_1")}
          </p>
          <p className="text-matte/65 leading-relaxed text-lg max-w-2xl mt-8">
            {c("programs_overview_text_2")}
          </p>
        </FadeUp>
      </section>

      {/* PREMIUM FEATURES */}
      <section className="bg-cream">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <FadeUp><Eyebrow>What's Included</Eyebrow></FadeUp>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-12 mt-12">
            {[
              ["A cohort of four", "A maximum of four students per cohort — small-group learning where every child is truly known."],
              ["A private session", "One private, one-on-one session with an educator is included during the four-week program."],
              ["A studio of their own", "Each child receives their own professional art materials, a Brainwaves portfolio bag, and an Explorer Kit to keep."],
              ["A bonsai to tend", "Every child cares for their own bonsai tree throughout the program — a quiet lesson in responsibility, observation, and patience."],
            ].map(([t, b], i) => (
              <FadeUp key={t} delay={i * 0.06}>
                <div className="border-t border-matte/20 pt-6">
                  <h3 className="font-serif text-2xl md:text-3xl font-light mb-3">{t}</h3>
                  <p className="text-matte/60 leading-relaxed max-w-md">{b}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAM DETAILS */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <FadeUp><Eyebrow>The Details</Eyebrow></FadeUp>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-matte/10 border border-matte/10 mt-10">
          {[
            ["Ages", "3–5"],
            ["Duration", "4-Week Program"],
            ["Schedule", "Twice Weekly · 3-Hour Sessions"],
            ["Cohort Size", "Maximum 4 Students"],
            ["Included", "One 1-Hour Private Session"],
            ["Disciplines", "Art · Science · Music · Nature · Storytelling"],
          ].map(([k, v]) => (
            <div key={k} className="bg-ivory p-7 md:p-8">
              <p className="text-[10px] uppercase tracking-editorial text-plum mb-3">{k}</p>
              <p className="font-serif text-lg md:text-2xl font-light leading-snug">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WONDER STUDIO SHOWCASE */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pb-4 md:pb-8">
        <FadeUp>
          <div className="bg-matte text-ivory px-8 md:px-16 py-16 md:py-24">
            <p className="text-[11px] uppercase tracking-editorial text-brass mb-6">{c("programs_showcase_eyebrow", "The Celebration")}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light mb-8 max-w-3xl">{c("programs_showcase_title", "Wonder Studio Showcase")}</h2>
            <p className="text-ivory/70 leading-relaxed text-lg max-w-2xl mb-10">
              {c("programs_showcase_text")}
            </p>
            <div className="flex flex-wrap gap-x-14 gap-y-6 border-t border-ivory/15 pt-8">
              <div>
                <p className="text-[10px] uppercase tracking-editorial text-brass mb-2">Date</p>
                <p className="font-serif text-2xl font-light">Thursday, September 17</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-editorial text-brass mb-2">Time</p>
                <p className="font-serif text-2xl font-light">6:00 PM</p>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* CLASSES */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pt-4 md:pt-8">
        <FadeUp>
          <Eyebrow>{c("programs_classes_eyebrow", "Classes")}</Eyebrow>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-3">{c("programs_classes_title", "Choose Your Session")}</h2>
        </FadeUp>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pb-24">
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-20">
          {programs.map((p, i) => (
            <FadeUp key={p.id} delay={(i % 2) * 0.1}>
              <div data-testid={`program-card-${p.id}`} className="group">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <Eyebrow>{p.subtitle}</Eyebrow>
                    <h3 className="font-serif text-3xl md:text-4xl font-light mt-2">{p.title}</h3>
                  </div>
                  <span className="font-serif text-2xl text-plum whitespace-nowrap">{money(p.price)}</span>
                </div>
                <p className="text-matte/60 leading-relaxed mt-4 max-w-lg">{p.description}</p>
                <div className="flex flex-wrap gap-x-8 gap-y-2 mt-5 text-[11px] uppercase tracking-wide-sm text-matte/50">
                  <span>Ages {p.ages}</span>
                  <span>{p.duration}</span>
                  {(p.days || p.schedule) && <span>{p.days || p.schedule}</span>}
                  {fmtRange(p.start_time, p.end_time) && <span data-testid={`program-time-${p.id}`}>{fmtRange(p.start_time, p.end_time)}</span>}
                </div>
                <Link
                  to={`/enroll?program=${p.id}`}
                  data-testid={`enroll-btn-${p.id}`}
                  className="group/btn inline-flex items-center gap-3 mt-7 bg-matte text-ivory px-8 py-3.5 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors duration-300"
                >
                  Enroll
                  <ArrowRight size={15} className="group-hover/btn:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}
